"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PushSendMessageSchema = exports.PushRegisterRequestSchema = exports.StartMobileDeployRequestSchema = exports.SetMobileNativeRequestSchema = exports.RegisterMobileBuildRequestSchema = exports.PutMobileAppMenuRequestSchema = exports.MobileAppMenuSchema = exports.MobileAppMenuEntrySchema = exports.MOBILE_PURCHASE_ROUTES = exports.PatchMobileDeployRequestSchema = exports.ActivateMobileDeployRequestSchema = exports.PutMobileRulesRequestSchema = exports.MobileRulesQuerySchema = exports.MobileCheckInResponseSchema = exports.MobileCheckInRequestSchema = exports.MobileWebDeploySchema = exports.MOBILE_DEPLOY_STATUSES = exports.MobileBundleRefSchema = exports.MobileUpdateRulesSchema = exports.MobileApplyModeSchema = exports.MobilePermissionsSchema = exports.MOBILE_PERMISSION_STATES = exports.MobilePlatformSchema = exports.MOBILE_PLATFORMS = exports.MobileAppIdSchema = exports.MOBILE_APPS = void 0;
exports.appMenuAllowedRoutes = appMenuAllowedRoutes;
exports.classifyMobileBuild = classifyMobileBuild;
const zod_1 = require("zod");
const releases_1 = require("./releases");
/**
 * RubyTill phone apps (spec 2026-09-29-rubytill-mobile-apps-design.md).
 * `rubytill` wraps platform-app (merchant pool); `rubytill-admin` wraps
 * super-admin-ui (admin pool). Adding an app is additive; never rename these.
 */
exports.MOBILE_APPS = ['rubytill', 'rubytill-admin'];
exports.MobileAppIdSchema = zod_1.z.enum(exports.MOBILE_APPS);
exports.MOBILE_PLATFORMS = ['ios', 'android'];
exports.MobilePlatformSchema = zod_1.z.enum(exports.MOBILE_PLATFORMS);
/** What the OS reports. `unavailable` = no hardware (e.g. no biometrics). */
exports.MOBILE_PERMISSION_STATES = ['granted', 'denied', 'prompt', 'unavailable'];
const PermissionState = zod_1.z.enum(exports.MOBILE_PERMISSION_STATES);
/** Exactly the three permissions the apps ask for (spec §6). Absent = not reported. */
exports.MobilePermissionsSchema = zod_1.z.object({
    notifications: PermissionState.optional(),
    camera: PermissionState.optional(),
    biometrics: PermissionState.optional(),
}).strict();
exports.MobileApplyModeSchema = zod_1.z.enum(['instant', 'next-open']);
const nativeBuild = zod_1.z.number().int().nonnegative();
const sha256Hex = zod_1.z.string().regex(/^[a-f0-9]{64}$/, 'must be lowercase hex sha256');
const bundleId = zod_1.z.string().min(1).max(64);
/**
 * Per app+env+platform. Native versions compare by BUILD NUMBER only
 * (iOS CFBundleVersion / Android versionCode) — never parse version strings.
 * `storeUrl` absent → the shell uses the store link compiled into it.
 */
exports.MobileUpdateRulesSchema = zod_1.z.object({
    requiredNativeBuild: nativeBuild.default(0),
    recommendedNativeBuild: nativeBuild.default(0),
    applyMode: exports.MobileApplyModeSchema.default('next-open'),
    storeUrl: zod_1.z.string().url().optional(),
    updatedAt: zod_1.z.number().int().nonnegative().optional(),
    updatedBy: zod_1.z.string().max(200).optional(),
});
exports.MobileBundleRefSchema = zod_1.z.object({
    bundleId,
    bundleUrl: zod_1.z.string().url(),
    bundleHash: sha256Hex,
});
exports.MOBILE_DEPLOY_STATUSES = ['active', 'paused', 'complete', 'rolled-back'];
const DeployStatus = zod_1.z.enum(exports.MOBILE_DEPLOY_STATUSES);
/**
 * The LIVE web (over-the-air) deploy for one app+env+platform.
 * `previous` is what phones on this bundle return to on rollback;
 * null = the bundle built into the store app.
 */
exports.MobileWebDeploySchema = exports.MobileBundleRefSchema.extend({
    deployId: zod_1.z.string().min(1).max(64),
    app: exports.MobileAppIdSchema,
    platform: exports.MobilePlatformSchema,
    env: releases_1.ReleaseEnvSchema,
    minNativeBuild: nativeBuild,
    percent: zod_1.z.number().int().min(0).max(100),
    status: DeployStatus,
    previous: exports.MobileBundleRefSchema.nullable(),
    /**
     * Bundles rolled back on EARLIER deploys, carried forward on every activation
     * (newest last, max 10). A phone still running one of these — offline during
     * the rollback — is rescued on check-in regardless of rollout percent.
     */
    rolledBackBundleIds: zod_1.z.array(bundleId).max(10).default([]),
    /** Step 10 → 50 → 100 automatically while healthy (spec §4.3). Absent on older deploys = off. */
    autoAdvance: zod_1.z.boolean().default(false),
    startedOk: zod_1.z.number().int().nonnegative().default(0),
    failedStarts: zod_1.z.number().int().nonnegative().default(0),
    createdAt: zod_1.z.number().int().nonnegative(),
    updatedAt: zod_1.z.number().int().nonnegative(),
    updatedBy: zod_1.z.string().max(200).optional(),
});
exports.MobileCheckInRequestSchema = zod_1.z.object({
    app: exports.MobileAppIdSchema,
    platform: exports.MobilePlatformSchema,
    env: releases_1.ReleaseEnvSchema,
    deviceId: zod_1.z.string().uuid(),
    nativeVersion: zod_1.z.string().min(1).max(32),
    nativeBuild,
    /** null = running the bundle built into the store app. */
    bundleId: bundleId.nullable(),
    /** Result of the last bundle switch; sent once, cleared by the shell after a 200. */
    lastApply: zod_1.z.object({ bundleId, outcome: zod_1.z.enum(['ok', 'reverted']) }).optional(),
    permissions: exports.MobilePermissionsSchema.default({}),
    osVersion: zod_1.z.string().max(32).optional(),
    model: zod_1.z.string().max(64).optional(),
});
exports.MobileCheckInResponseSchema = zod_1.z.object({
    gate: zod_1.z.enum(['ok', 'recommended', 'required']),
    storeUrl: zod_1.z.string().url().nullable(),
    bundle: zod_1.z.object({ bundleId, url: zod_1.z.string().url(), sha256: sha256Hex }).nullable(),
    /** true = drop any downloaded bundle and run the built-in one (rollback with no previous). */
    resetToBuiltin: zod_1.z.boolean(),
    applyMode: exports.MobileApplyModeSchema,
    checkedAt: zod_1.z.number().int().nonnegative(),
});
const target = { app: exports.MobileAppIdSchema, env: releases_1.ReleaseEnvSchema, platform: exports.MobilePlatformSchema };
exports.MobileRulesQuerySchema = zod_1.z.object(target);
exports.PutMobileRulesRequestSchema = zod_1.z.object({
    ...target,
    rules: exports.MobileUpdateRulesSchema.omit({ updatedAt: true, updatedBy: true }),
}).refine((b) => b.rules.recommendedNativeBuild >= b.rules.requiredNativeBuild, {
    message: 'Recommended version must be at or above the required version',
    path: ['rules', 'recommendedNativeBuild'],
});
exports.ActivateMobileDeployRequestSchema = exports.MobileBundleRefSchema.extend({
    ...target,
    deployId: zod_1.z.string().min(1).max(64),
    minNativeBuild: nativeBuild,
    percent: zod_1.z.number().int().min(0).max(100),
    autoAdvance: zod_1.z.boolean().optional(),
});
exports.PatchMobileDeployRequestSchema = zod_1.z.object({
    ...target,
    deployId: zod_1.z.string().min(1).max(64),
    percent: zod_1.z.number().int().min(0).max(100).optional(),
    status: DeployStatus.optional(),
}).refine((b) => b.percent !== undefined || b.status !== undefined, {
    message: 'Send a percent or a status',
});
// ─── App menus (P4) ──────────────────────────────────────────────────────────
/**
 * Surfaces that sell or upgrade: never in a phone app, whatever the switches
 * say (Apple 3.1.1 — spec D6). Prefix match: a child route is locked too.
 */
exports.MOBILE_PURCHASE_ROUTES = {
    rubytill: [
        '/subscription-setup', '/pos/billing', '/card-payments', '/creator-studio/credits',
        '/order-devices', '/pos/marketplace-admin', '/affiliate-program', '/super-admin/billing',
    ],
    'rubytill-admin': [],
};
exports.MobileAppMenuEntrySchema = zod_1.z.object({
    /** Stable id of the menu item it came from (sidebar item id / admin nav path). */
    id: zod_1.z.string().min(1).max(120),
    label: zod_1.z.string().min(1).max(120),
    group: zod_1.z.string().max(120).default(''),
    /** Absent for a pure group row. Must be an in-app path. */
    route: zod_1.z.string().regex(/^\/[^\s?#]*$/, 'route must be an in-app path').max(200).optional(),
    ios: zod_1.z.boolean(),
    android: zod_1.z.boolean(),
});
exports.MobileAppMenuSchema = zod_1.z.object({
    entries: zod_1.z.array(exports.MobileAppMenuEntrySchema).max(500),
    updatedAt: zod_1.z.number().int().nonnegative().optional(),
    updatedBy: zod_1.z.string().max(200).optional(),
});
exports.PutMobileAppMenuRequestSchema = zod_1.z.object({
    app: exports.MobileAppIdSchema,
    env: releases_1.ReleaseEnvSchema,
    entries: zod_1.z.array(exports.MobileAppMenuEntrySchema).max(500),
});
const isPurchase = (route, locked) => locked.some((p) => route === p || route.startsWith(`${p}/`));
/** The route prefixes a build for this app+platform may contain (the build's __APP_MENU__). */
function appMenuAllowedRoutes(menu, app, platform) {
    const locked = exports.MOBILE_PURCHASE_ROUTES[app] ?? [];
    const out = [];
    for (const e of menu.entries) {
        if (!e.route || !e[platform] || isPurchase(e.route, locked) || out.includes(e.route))
            continue;
        out.push(e.route);
    }
    return out;
}
// ─── Candidate builds, native fingerprint, deploy dispatch (P6) ──────────────
const commitSha = zod_1.z.string().regex(/^[0-9a-f]{7,64}$/, 'commit must be a hex sha');
/** CI (internal key) registers every deployed web build as a mobile candidate. */
exports.RegisterMobileBuildRequestSchema = zod_1.z.object({
    app: exports.MobileAppIdSchema,
    env: releases_1.ReleaseEnvSchema,
    commit: commitSha,
    commitMessage: zod_1.z.string().max(200).optional(),
    /** sha256 of the native layer (capacitor config, plugin versions, ios/android project files). */
    nativeFingerprint: sha256Hex,
    builtAt: zod_1.z.number().int().nonnegative(),
});
/** The native build live in the store for a platform: web builds must match its fingerprint. */
exports.SetMobileNativeRequestSchema = zod_1.z.object({
    app: exports.MobileAppIdSchema,
    env: releases_1.ReleaseEnvSchema,
    platform: exports.MobilePlatformSchema,
    fingerprint: sha256Hex,
    nativeBuild,
    nativeVersion: zod_1.z.string().min(1).max(32),
});
/** Super Admin ▸ Mobile Apps ▸ Deploy (web update) → dispatches mobile-release.yml. */
exports.StartMobileDeployRequestSchema = zod_1.z.object({
    app: exports.MobileAppIdSchema,
    env: releases_1.ReleaseEnvSchema,
    platform: exports.MobilePlatformSchema,
    commit: commitSha,
    percent: zod_1.z.number().int().min(1).max(100),
    autoAdvance: zod_1.z.boolean(),
    minNativeBuild: nativeBuild,
});
/** Web update when the native layer is unchanged from what is live in the store; otherwise it needs a store release. */
function classifyMobileBuild(fingerprint, liveNativeFingerprint) {
    return liveNativeFingerprint !== null && fingerprint === liveNativeFingerprint ? 'web' : 'native';
}
// ─── Push notifications (P5) ─────────────────────────────────────────────────
/** POST /push/register (merchant token). The tenant list comes from the token's claim, never the body. */
exports.PushRegisterRequestSchema = zod_1.z.object({
    deviceId: zod_1.z.string().uuid(),
    app: exports.MobileAppIdSchema,
    platform: exports.MobilePlatformSchema,
    /** FCM registration token (Android) or APNs device token (iOS). */
    token: zod_1.z.string().min(1).max(4096),
});
/** A push-send queue message (producers → push-service). */
exports.PushSendMessageSchema = zod_1.z.object({
    /** Cognito subs (the push-token owner). Optional since emails were added. */
    userIds: zod_1.z.array(zod_1.z.string().min(1).max(128)).max(500).default([]),
    /**
     * Recipient emails (lower-cased). Tenant user records use their own ids,
     * NOT the Cognito sub, so producers that only know user records address by
     * the email both sides share.
     */
    emails: zod_1.z.array(zod_1.z.string().email().max(254).transform((e) => e.toLowerCase())).max(500).default([]),
    tenantId: zod_1.z.string().min(1).max(128),
    title: zod_1.z.string().min(1).max(120),
    body: zod_1.z.string().min(1).max(500),
    /** In-app route opened on tap; goes through the app-menu guard on the phone. */
    deepLink: zod_1.z.string().regex(/^\/[^\s]*$/, 'deepLink must be an in-app path').max(300).optional(),
    /**
     * Notification category (same keys as EMAIL_CATEGORIES, e.g. 'webReadiness')
     * so a user's opt-out can apply to push too. Producers check the opt-out
     * before enqueueing; push-service does not filter yet.
     */
    category: zod_1.z.string().max(64).optional(),
}).refine((m) => m.userIds.length + m.emails.length > 0, { message: 'Send to at least one user id or email' });
//# sourceMappingURL=mobile.js.map